import {
  Button,
  Dialog,
  DialogActions,
  DialogContent
} from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import React, { useState, useCallback } from 'react';
import { useNotify, FormDataConsumer, SaveButton } from 'react-admin';
import { FormSpy } from 'react-final-form';

import MailApi from '../../../../core/apis/MailApi';
import DialogTitleWithCloseButton from '../../../../core/components/DialogTitleWithCloseButton';
import EmailForm from './EmailForm';
import DistributionListContactDialog from '../../../testemail/DistributionListContactDialog';
import EmailPreviewDialog from './EmailPreviewDialog';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import dataProvider from '../../../../core/providers/IccUserDataProvider';
import CustomSimpleForm from '../../../../core/components/form/CustomSimpleForm';
import LogApi from '../../../../../src/core/apis/LogApi';
import { getIcError } from '../../../../core/providers/utils';
import Confirm from '../../../../core/components/forked/layout/Confirm';

const styles = {
  paperWidthFalse: {
    height: 'inherit'
  },
  dialogContent: {
    padding: '0px'
  },
  leftWrapper: {
    flexGrow: 1
  },
  button: {
    '& span': {
      padding: 0
    }
  },
  primaryButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    }
  },
  secondaryButton: {
    border: '1px solid #E0E0E0',
    '& span': {
      padding: 0
    },
    marginLeft: '10px'
  },
  form: {
    height: 'inherit',
    '& > div': {
      padding: '0px !important',
      height: 'inherit'
    },
    '& > div > div': {
      display: 'flex',
      flexDirection: 'column',
      height: 'inherit'
    }
  }
};

const getBase64 = file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function() {
      const b64 = reader.result.replace(/^data:.+;base64,/, '');
      resolve(b64);
    };
  });
};

const splitEmails = value => {
  if (value) {
    const emailParts = value.split(';');
    return emailParts
      .filter(part => part.trim())
      .map(emailAddress => ({
        name: null,
        address: emailAddress.trim()
      }));
  }
  return [];
};

const EmailDialog = ({
  handleClose,
  resourceuploadId,
  notificationMethod = 'REPORTING',
  open,
  classes,
  recipientsList = [],
  recipientsListSelected,
  variableType = 'deliveryTab',
  userProfile
}) => {
  const [
    showDistributionListTestEmailDialog,
    setShowDistributionListTestEmailDialog
  ] = useState(false);
  const [showPreviewEmailDialog, setShowPreviewEmailDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previousNotificationMethod, setPreviousNotificationMethod] = useState(
    ''
  );
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();

  const [savedFormProps, setSavedFormProps] = useState(null);
  const [openBanner, setOpenBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const toggleShowDistributionListTestEmailDialog = useCallback(
    () => setShowDistributionListTestEmailDialog(prevState => !prevState),
    []
  );
  const toggleShowPreviewEmailDialog = useCallback(
    () => setShowPreviewEmailDialog(prevState => !prevState),
    []
  );

  function getUniqueInvestors(selectedItems) {
    const uniqueInvestors = new Map();

    selectedItems.forEach(item => {
      uniqueInvestors.set(item.investorId, item.investorName);
    });

    return Array.from(uniqueInvestors, ([investorId, investorName]) => ({
      investorId,
      investorName
    }));
  }

  const handleOpenBanner = formprops => {
    let investors = getUniqueInvestors(formprops.selectedItems);

    const hasAllInvestors = investors.some(
      investor =>
        investor.investorName &&
        investor.investorName.toLowerCase() === 'all investors'
    );
    const hasMultipleInvestors = investors.length > 1;
    const hasAttachments =
      formprops.hasOwnProperty('attachments') &&
      formprops.attachments.length > 0;

    if (hasAllInvestors || hasMultipleInvestors || hasAttachments) {
      setSavedFormProps(formprops);
      setOpenBanner(true);
    } else {
      onSubmit(formprops);
    }
  };

  const handleCloseBanner = () => {
    setSavedFormProps(null);
    setOpenBanner(false);
  };

  /**
   * Send Email to distribution List
   * @param values: values of the email created
   * @param distributionItemsSelected
   */
  const onSubmit = useCallback(
    async values => {
      LogApi.sendClientlogs(
        LogApi.createTrackingObject(
          null,
          null,
          `resourceUploadId: ${values?.resourceUploadId}, subject: ${values?.subject}`,
          null,
          null,
          `POST v1/mail/sendmailtolist`,
          `Delivery Tab Send Email Button Handler`
        )
      ).catch(err => {
        console.error('Server error: ' + getIcError(err));
      });
      setIsLoading(true);
      setSubmitting(true);
      const groupsDict = {};

      const sendingList = values.selectedItems.reduce((acc, curr) => {
        const groupId = `${curr.documentId}:${curr.fundId}:${curr.investorId}`;
        let group = groupsDict[groupId];
        if (!group) {
          group = {
            documentId: curr.documentId,
            documentFileName: curr.documentFileName,
            fundId: curr.fundId,
            investorId: curr.investorId,
            users: []
          };
          groupsDict[groupId] = group;
          acc.push(group);
        }
        group.users.push({
          userId: curr.userId,
          email: curr.email,
          firstname: curr.firstname,
          lastname: curr.lastname,
          fax: curr.fax,
          address: curr.address,
          ccEmails: curr.ccEmails
        });
        return acc;
      }, []);

      const attachments = await (values.attachments || []).map(async item => {
        const base64Content = await getBase64(item.rawFile);
        return {
          content: base64Content,
          mimeType: item.type,
          fileName: item.title,
          disposition: null,
          contentId: null
        };
      });

      const listToSend = {
        resourceUploadId: resourceuploadId,
        notificationMethod: notificationMethod,
        from: {
          fromAddress: {
            name: values.alias,
            address: values.from
          },
          replyToAddress: {
            name: null,
            address: values.aliasTo
          },
          rememberEmailAddressAlias: values.rememberMe
        },
        to: sendingList,
        cc: splitEmails(values.cc),
        bcc: splitEmails(values.bcc),
        subject: values.subject,
        msgBody: values.msgBody,
        attachments: await Promise.all(attachments),
        isTestEmail: false,
        emailTemplateId: values.templateId
      };

      if (
        values.rememberMe &&
        (values.alias !== null || values.aliasTo !== null)
      ) {
        try {
          await dataProvider('UPDATE', 'aliasUdfs', {
            data: {
              emailAlias: values.alias || '',
              emailAliasReplyTo: values.aliasTo || ''
            }
          });
        } catch (e) {
          notifyServerError(e);
        }
      }
      try {
        await MailApi.sendMailToList(listToSend);
        setSubmitting(false);
        handleClose();
        notify('ra.notification.distribution_email_sent');
      } catch (e) {
        setSubmitting(false);
        notifyServerError(e);
      }
      setOpenBanner(false);
      setIsLoading(false);
      setSavedFormProps(null);
    },
    [
      resourceuploadId,
      notificationMethod,
      handleClose,
      notify,
      notifyServerError
    ]
  );

  return (
    <CustomSimpleForm
      toolbar={null}
      resource="sendmailtolist"
      variant="standard"
      save={handleOpenBanner}
      className={classes.form}
      initialValues={{ selectedItems: recipientsListSelected }}
      keepDirtyOnReinitialize={false}
    >
      <FormSpy subscription={{ values: true, pristine: true }}>
        {formprops => {
          return (
            <Dialog
              open={open}
              aria-label="Edit Email"
              maxWidth={false}
              onClose={(event, reason) => {
                if (reason === 'backdropClick') return null;
                handleClose();
              }}
              disableEnforceFocus={true}
              classes={{
                paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
              }}
            >
              <DialogTitleWithCloseButton onClose={handleClose}>
                Edit Email
              </DialogTitleWithCloseButton>
              <DialogContent>
                <EmailForm
                  variant="standard"
                  choices={recipientsList}
                  groupBy="investorId"
                  groupLabel="investorName"
                  recipientId={['userId', 'fundId', 'investorId']}
                  textButton="Document Distribution List Batch"
                  variableType={variableType}
                  userProfile={userProfile}
                  notificationMethod={notificationMethod}
                  setPreviousNotificationMethod={setPreviousNotificationMethod}
                  previousNotificationMethod={previousNotificationMethod}
                  useFundFromEmail={true}
                  useManualEmailFrom={false}
                />
                <FormDataConsumer>
                  {({ formData, ...rest }) =>
                    formData && (
                      <DistributionListContactDialog
                        title=" "
                        showDialog={showDistributionListTestEmailDialog}
                        handleDialogToggle={
                          toggleShowDistributionListTestEmailDialog
                        }
                        formData={formData}
                        {...formprops}
                        deliveryTab={true}
                        notificationMethod={notificationMethod}
                        resourceuploadId={resourceuploadId}
                      />
                    )
                  }
                </FormDataConsumer>

                <FormDataConsumer>
                  {({ formData, ...rest }) =>
                    formData && (
                      <EmailPreviewDialog
                        title=" "
                        showDialog={showPreviewEmailDialog}
                        handleDialogToggle={toggleShowPreviewEmailDialog}
                        formData={formData}
                        resourceuploadId={resourceuploadId}
                        {...formprops}
                      />
                    )
                  }
                </FormDataConsumer>
              </DialogContent>
              <DialogActions
                style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}
              >
                {/* <div className={classes.leftWrapper}>
                    <Button
                      onClick={this.handleBackForm}
                      variant="outlined"
                      disabled={true}
                      color="primary"
                      className={classes.testButton}
                    >
                      Test Email
                    </Button>
                  </div> */}
                <div className={classes.leftWrapper}>
                  <Button
                    onClick={toggleShowDistributionListTestEmailDialog}
                    variant="outlined"
                    color="primary"
                    className={classes.primaryButton}
                  >
                    Test Email
                  </Button>
                  <Button
                    onClick={toggleShowPreviewEmailDialog}
                    variant="outlined"
                    color="primary"
                    className={classes.secondaryButton}
                  >
                    Preview Email
                  </Button>
                </div>
                <SaveButton
                  saving={submitting}
                  handleSubmitWithRedirect={formprops.form.submit}
                  color="primary"
                  disabled={submitting}
                  variant="contained"
                  className={classes.button}
                  label={'Send Email'}
                  icon={<></>}
                />
              </DialogActions>
            </Dialog>
          );
        }}
      </FormSpy>
      <Confirm
        isOpen={openBanner}
        loading={isLoading}
        title="Warning"
        content={
          <p style={{ margin: 0 }}>
            More than one investor has been selected.
            <br />
            Please make sure that this email and attachments are targeted to the
            intended recipients.
          </p>
        }
        onConfirm={() => onSubmit(savedFormProps)}
        onClose={handleCloseBanner}
        confirm="Send"
      />
    </CustomSimpleForm>
  );
};

export default withStyles(styles)(EmailDialog);
